import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useToast } from '../hooks/use-toast';
import { PasswordField } from './PasswordPages';

const inputCls = 'w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-[#3a9b5c] focus:ring-2 focus:ring-[#3a9b5c]/20';
const labelCls = 'mb-1.5 block text-sm font-medium text-gray-700';

const AuthPage = () => {
  const { login, register } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get('mode') === 'register' ? 'register' : 'login');
  const [role, setRole] = useState(params.get('role') || 'client');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const next = params.get('next');

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const go = (u) => {
    if (next) return navigate(next);
    if (u.isAdmin) return navigate('/admin');
    navigate(u.role === 'operative' ? '/dashboard/operative' : '/dashboard/client');
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = mode === 'login'
        ? await login(form.email, form.password)
        : await register({ ...form, role });
      toast({ title: mode === 'login' ? 'Welcome back' : 'Account created' });
      if (mode === 'register' && role === 'operative' && !next) {
        navigate('/profile/operative');
      } else {
        go(u);
      }
    } catch (err) {
      let description = 'Please try again.';
      if (err?.response?.data?.detail) {
        // Server responded with a specific error (e.g. duplicate email, bad password)
        description = err.response.data.detail;
      } else if (err?.response?.status) {
        description = `Server error (${err.response.status}). Please try again in a moment.`;
      } else if (err?.request) {
        // Request was made but no response received (network / connectivity / wrong host)
        description = 'Could not reach the server. Please check your connection and try again.';
      }
      console.error('Auth error:', err?.response?.status, err?.message, err?.response?.data);
      toast({ title: 'Error', description, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] bg-[#e9f3ec] py-14">
      <div className="mx-auto max-w-md px-5">
        <div className="rounded-2xl bg-white p-8 shadow-md">
          <h1 className="ea-heading text-2xl font-bold">{mode === 'login' ? 'Log In' : 'Create Your Account'}</h1>
          <p className="mt-1 text-sm text-gray-500">{mode === 'login' ? 'Access your marketplace dashboard' : 'Join the EA Lead Solutions marketplace'}</p>

          {mode === 'register' && (
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button type="button" data-testid="role-client-btn" onClick={() => setRole('client')} className={`rounded-lg border p-3 text-left text-sm ${role === 'client' ? 'border-[#3a9b5c] bg-[#e9f3ec] font-semibold text-[#1b4f72]' : 'border-gray-200 text-gray-600'}`}>I'm looking for a professional<span className="mt-1 block text-xs font-normal text-gray-400">Client</span></button>
              <button type="button" data-testid="role-operative-btn" onClick={() => setRole('operative')} className={`rounded-lg border p-3 text-left text-sm ${role === 'operative' ? 'border-[#3a9b5c] bg-[#e9f3ec] font-semibold text-[#1b4f72]' : 'border-gray-200 text-gray-600'}`}>I'm a professional looking for work<span className="mt-1 block text-xs font-normal text-gray-400">Operative</span></button>
            </div>
          )}

          <form onSubmit={submit} className="mt-5 space-y-4">
            {mode === 'register' && (
              <div><label className={labelCls}>Full Name</label><input required data-testid="auth-name" className={inputCls} value={form.name} onChange={(e) => upd('name', e.target.value)} /></div>
            )}
            <div><label className={labelCls}>Email</label><input required data-testid="auth-email" type="email" className={inputCls} value={form.email} onChange={(e) => upd('email', e.target.value)} /></div>
            <div><label className={labelCls}>Password</label><PasswordField value={form.password} onChange={(e) => upd('password', e.target.value)} testid="auth-password" /></div>
            <button disabled={busy} data-testid="auth-submit-btn" className="w-full rounded-full bg-[#3a9b5c] px-6 py-3 font-semibold text-white transition-colors hover:bg-[#318150] disabled:opacity-60">{busy ? 'Please wait...' : (mode === 'login' ? 'Log In' : 'Create Account')}</button>
            {mode === 'login' && <Link to="/forgot-password" data-testid="forgot-password-link" className="block text-center text-sm text-[#3a9b5c] underline">Forgot your password?</Link>}
          </form>

          <button type="button" data-testid="google-signin-btn" onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + '/auth/callback';
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }} className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-gray-300 px-6 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50">
            <span className="text-[#4285F4] font-bold">G</span> Continue with Google
          </button>

          <p className="mt-5 text-center text-sm text-gray-500">
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button type="button" onClick={() => setMode(mode === 'login' ? 'register' : 'login')} className="font-semibold text-[#3a9b5c]">{mode === 'login' ? 'Sign up' : 'Log in'}</button>
          </p>
          <p className="mt-3 text-center text-xs text-gray-400"><Link to="/">← Back to website</Link></p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
